(() => {
  'use strict';

  const SIMULATION_PROPERTY = '_inspira_frete_simulacao';

  const MAX_POLL_ATTEMPTS = 20;
  const POLL_INTERVAL_MS = 750;

  /*
    A taxa Frete Grátis foi configurada na Shopify
    com prazo de 5 a 8 dias úteis.

    A Entrega Local não necessariamente retorna
    prazo pela Ajax Cart API. Enquanto não houver
    um prazo comercial definido, mostramos
    "Prazo sob consulta".
  */
  const FREE_SHIPPING_FALLBACK = '5 a 8 dias úteis';
  const LOCAL_DELIVERY_FALLBACK = 'Prazo sob consulta';


  /* ==========================================================
     HELPERS
     ========================================================== */

  function onlyDigits(value) {
    return String(value || '').replace(/\D/g, '');
  }


  function formatPostalCode(value) {
    const digits = onlyDigits(value).slice(0, 8);

    if (digits.length <= 5) {
      return digits;
    }

    return digits.slice(0, 5) + '-' + digits.slice(5);
  }


  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }


  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }


  function formatMoney(value) {
    const number = Number(value || 0);

    return new Intl.NumberFormat(
      'pt-BR',
      {
        style: 'currency',
        currency: 'BRL'
      }
    ).format(number);
  }


  function getShopifyRoot() {
    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {
      return window.Shopify.routes.root;
    }

    return '/';
  }


  function makeSimulationId() {
    if (
      window.crypto &&
      typeof window.crypto.randomUUID === 'function'
    ) {
      return window.crypto.randomUUID();
    }

    return (
      Date.now().toString(36) +
      '-' +
      Math.random().toString(36).slice(2)
    );
  }


  /* ==========================================================
     CEP
     ========================================================== */

  const BRAZIL_STATES = {
    AC: 'Acre',
    AL: 'Alagoas',
    AP: 'Amapá',
    AM: 'Amazonas',
    BA: 'Bahia',
    CE: 'Ceará',
    DF: 'Distrito Federal',
    ES: 'Espírito Santo',
    GO: 'Goiás',
    MA: 'Maranhão',
    MT: 'Mato Grosso',
    MS: 'Mato Grosso do Sul',
    MG: 'Minas Gerais',
    PA: 'Pará',
    PB: 'Paraíba',
    PR: 'Paraná',
    PE: 'Pernambuco',
    PI: 'Piauí',
    RJ: 'Rio de Janeiro',
    RN: 'Rio Grande do Norte',
    RS: 'Rio Grande do Sul',
    RO: 'Rondônia',
    RR: 'Roraima',
    SC: 'Santa Catarina',
    SP: 'São Paulo',
    SE: 'Sergipe',
    TO: 'Tocantins'
  };


  async function resolvePostalCode(postalCode) {
    const cep = onlyDigits(postalCode);

    const response = await fetch(
      `https://viacep.com.br/ws/${cep}/json/`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Não foi possível consultar o CEP.'
      );
    }

    const data = await response.json();

    if (
      data.erro ||
      !data.uf
    ) {
      throw new Error(
        'CEP não encontrado.'
      );
    }

    const stateCode =
      String(data.uf).toUpperCase();

    return {
      zip: cep,

      country: 'Brazil',

      province:
        BRAZIL_STATES[stateCode] ||
        stateCode,

      provinceCode:
        stateCode,

      city:
        data.localidade || ''
    };
  }


  /* ==========================================================
     PRODUTO / VARIANTE
     ========================================================== */

  function getProductScope(root) {
    return (
      root.closest('.product') ||
      root.closest('product-info') ||
      root.closest('section') ||
      document
    );
  }


  function getCurrentVariantId(root) {
    const scope = getProductScope(root);

    const variantInput =
      scope.querySelector(
        'form[action*="/cart/add"] [name="id"]'
      ) ||
      document.querySelector(
        'form[action*="/cart/add"] [name="id"]'
      );

    if (
      variantInput &&
      variantInput.value
    ) {
      return String(
        variantInput.value
      );
    }

    return String(
      root.dataset.defaultVariantId || ''
    );
  }


  function getQuantity(root) {
    const scope = getProductScope(root);

    const input =
      scope.querySelector(
        'input[name="quantity"]'
      ) ||
      scope.querySelector(
        '.quantity__input'
      );

    if (!input) {
      return 1;
    }

    const quantity =
      parseInt(input.value, 10);

    if (
      !Number.isFinite(quantity) ||
      quantity < 1
    ) {
      return 1;
    }

    return quantity;
  }


  /* ==========================================================
     AJAX CART API
     ========================================================== */

  async function getCart() {
    const response = await fetch(
      `${getShopifyRoot()}cart.js`,
      {
        method: 'GET',

        headers: {
          Accept: 'application/json'
        },

        credentials: 'same-origin',

        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(
        'Não foi possível consultar o carrinho.'
      );
    }

    return response.json();
  }


  async function removeCartLine(lineKey) {
    if (!lineKey) {
      return;
    }

    const response = await fetch(
      `${getShopifyRoot()}cart/change.js`,
      {
        method: 'POST',

        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },

        credentials: 'same-origin',

        body: JSON.stringify({
          id: String(lineKey),
          quantity: 0
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        'Não foi possível remover a simulação do carrinho.'
      );
    }
  }


  async function removeCartLineWithRetry(lineKey) {
    if (!lineKey) {
      return;
    }

    let lastError = null;

    for (
      let attempt = 0;
      attempt < 3;
      attempt++
    ) {
      try {
        await removeCartLine(lineKey);
        return;

      } catch (error) {
        lastError = error;

        await sleep(
          250 * (attempt + 1)
        );
      }
    }

    throw lastError;
  }


  async function cleanupStaleSimulations() {
    const cart = await getCart();

    const staleItems =
      (cart.items || []).filter(
        item =>
          item.properties &&
          item.properties[
            SIMULATION_PROPERTY
          ]
      );

    for (const item of staleItems) {
      try {
        await removeCartLineWithRetry(
          item.key
        );

      } catch (error) {
        console.warn(
          'Não foi possível remover uma simulação antiga:',
          error
        );
      }
    }
  }


  async function addTemporaryProduct(
    variantId,
    quantity
  ) {
    const simulationId =
      makeSimulationId();

    const response = await fetch(
      `${getShopifyRoot()}cart/add.js`,
      {
        method: 'POST',

        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },

        credentials: 'same-origin',

        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),

              quantity: quantity,

              properties: {
                [SIMULATION_PROPERTY]:
                  simulationId
              }
            }
          ]
        })
      }
    );

    let data = null;

    try {
      data = await response.json();
    } catch (_) {}

    if (!response.ok) {
      throw new Error(
        data &&
        (
          data.description ||
          data.message
        )
          ? (
              data.description ||
              data.message
            )
          : 'Não foi possível preparar o produto para o cálculo.'
      );
    }

    const item =
      data &&
      Array.isArray(data.items)
        ? data.items[0]
        : null;

    if (
      !item ||
      !item.key
    ) {
      throw new Error(
        'A Shopify não retornou a linha temporária do produto.'
      );
    }

    return {
      key: item.key,
      simulationId
    };
  }


  /* ==========================================================
     SHIPPING RATES
     ========================================================== */

  function buildShippingQuery(address) {
    const params =
      new URLSearchParams();

    params.set(
      'shipping_address[zip]',
      address.zip
    );

    params.set(
      'shipping_address[country]',
      address.country
    );

    if (address.province) {
      params.set(
        'shipping_address[province]',
        address.province
      );
    }

    if (address.city) {
      params.set(
        'shipping_address[city]',
        address.city
      );
    }

    return params.toString();
  }


  async function prepareShippingRates(address) {
    const query =
      buildShippingQuery(address);

    const response = await fetch(
      `${getShopifyRoot()}cart/prepare_shipping_rates.json?${query}`,
      {
        method: 'POST',

        headers: {
          Accept: 'application/json'
        },

        credentials: 'same-origin',

        cache: 'no-store'
      }
    );

    if (!response.ok) {
      let message =
        'Não foi possível iniciar o cálculo do frete.';

      try {
        const data =
          await response.json();

        if (
          data &&
          (
            data.description ||
            data.message
          )
        ) {
          message =
            data.description ||
            data.message;
        }

      } catch (_) {}

      throw new Error(message);
    }
  }


  async function getAsyncShippingRates(address) {
    const query =
      buildShippingQuery(address);

    const response = await fetch(
      `${getShopifyRoot()}cart/async_shipping_rates.json?${query}`,
      {
        method: 'GET',

        headers: {
          Accept: 'application/json'
        },

        credentials: 'same-origin',

        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(
        'Não foi possível obter as opções de frete.'
      );
    }

    return response.json();
  }


  async function waitForShippingRates(address) {
    for (
      let attempt = 0;
      attempt < MAX_POLL_ATTEMPTS;
      attempt++
    ) {
      const data =
        await getAsyncShippingRates(
          address
        );

      if (
        data !== null &&
        typeof data === 'object'
      ) {
        return Array.isArray(
          data.shipping_rates
        )
          ? data.shipping_rates
          : [];
      }

      await sleep(
        POLL_INTERVAL_MS
      );
    }

    throw new Error(
      'O cálculo do frete demorou mais que o esperado. Tente novamente.'
    );
  }


  /* ==========================================================
     NOMES / TRADUÇÃO
     ========================================================== */

  function getNormalizedRateName(rate) {
    const originalName =
      String(
        rate.presentment_name ||
        rate.name ||
        rate.code ||
        'Entrega'
      ).trim();

    const normalized =
      originalName.toLowerCase();

    /*
      A própria Shopify pode devolver esse método
      em inglês pela Ajax Cart API.
    */
    if (
      normalized === 'local delivery' ||
      normalized.includes('local delivery')
    ) {
      return 'Entrega Local';
    }

    return originalName;
  }


  function isLocalDelivery(rate) {
    const values = [
      rate.presentment_name,
      rate.name,
      rate.code,
      rate.source
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return (
      values.includes('local delivery') ||
      values.includes('local_delivery')
    );
  }


  function isFreeShipping(rate) {
    const name =
      getNormalizedRateName(rate)
        .toLowerCase();

    const price =
      Number(rate.price || 0);

    return (
      price <= 0 &&
      (
        name.includes('frete grátis') ||
        name.includes('frete gratis') ||
        name.includes('free shipping')
      )
    );
  }


  /* ==========================================================
     PRAZO
     ========================================================== */

  function formatDateBR(value) {
    if (!value) {
      return '';
    }

    try {
      const date =
        new Date(
          `${value}T12:00:00`
        );

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return '';
      }

      return new Intl.DateTimeFormat(
        'pt-BR',
        {
          day: '2-digit',
          month: '2-digit'
        }
      ).format(date);

    } catch (_) {
      return '';
    }
  }


  function getDeliveryLabel(rate) {
    /*
      Exemplo oficial da Ajax API:
      delivery_days: [0, 3]
    */
    if (
      Array.isArray(rate.delivery_days) &&
      rate.delivery_days.length
    ) {
      const values =
        rate.delivery_days
          .map(Number)
          .filter(Number.isFinite);

      if (values.length) {
        const min =
          Math.min(...values);

        const max =
          Math.max(...values);

        if (min === max) {
          return min === 1
            ? '1 dia útil'
            : `${min} dias úteis`;
        }

        return `${min} a ${max} dias úteis`;
      }
    }


    /*
      Algumas taxas retornam um intervalo
      de datas em delivery_range.
    */
    if (
      Array.isArray(rate.delivery_range) &&
      rate.delivery_range.length >= 2
    ) {
      const start =
        formatDateBR(
          rate.delivery_range[0]
        );

      const end =
        formatDateBR(
          rate.delivery_range[
            rate.delivery_range.length - 1
          ]
        );

      if (
        start &&
        end
      ) {
        if (start === end) {
          return `Entrega prevista para ${start}`;
        }

        return `Entrega prevista entre ${start} e ${end}`;
      }
    }


    /*
      Data única.
    */
    if (rate.delivery_date) {
      const date =
        formatDateBR(
          rate.delivery_date
        );

      if (date) {
        return `Entrega prevista para ${date}`;
      }
    }


    /*
      Caso o app/transportadora devolva
      uma descrição com prazo.
    */
    if (rate.description) {
      return String(
        rate.description
      );
    }


    /*
      Fallbacks comerciais.
    */
    if (isFreeShipping(rate)) {
      return FREE_SHIPPING_FALLBACK;
    }


    if (isLocalDelivery(rate)) {
      return LOCAL_DELIVERY_FALLBACK;
    }


    return 'Prazo sob consulta';
  }


  /* ==========================================================
     RENDER
     ========================================================== */

  function renderRate(rate) {
    const name =
      escapeHtml(
        getNormalizedRateName(rate)
      );

    const delivery =
      escapeHtml(
        getDeliveryLabel(rate)
      );

    const price =
      Number(rate.price || 0);

    const free =
      price <= 0;

    const priceLabel =
      free
        ? 'GRÁTIS'
        : formatMoney(price);


    return `
      <div class="inspira-shipping__rate">

        <div class="inspira-shipping__rate-info">

          <strong class="inspira-shipping__rate-name">
            ${name}
          </strong>

          <span class="inspira-shipping__rate-delivery">
            ${delivery}
          </span>

        </div>

        <strong
          class="inspira-shipping__rate-price${free ? ' is-free' : ''}"
        >
          ${priceLabel}
        </strong>

      </div>
    `;
  }


  /* ==========================================================
     CALCULADORA
     ========================================================== */

  function initCalculator(root) {
    if (
      root.dataset.shippingInitialized ===
      'true'
    ) {
      return;
    }

    const form =
      root.querySelector(
        '[data-inspira-shipping-form]'
      );

    const input =
      root.querySelector(
        '[data-inspira-shipping-cep]'
      );

    const button =
      root.querySelector(
        '[data-inspira-shipping-submit]'
      );

    const status =
      root.querySelector(
        '[data-inspira-shipping-status]'
      );

    const results =
      root.querySelector(
        '[data-inspira-shipping-results]'
      );

    if (
      !form ||
      !input ||
      !button ||
      !results
    ) {
      return;
    }

    root.dataset.shippingInitialized =
      'true';


    function setStatus(
      message,
      type
    ) {
      if (!status) {
        return;
      }

      status.textContent =
        message || '';

      status.classList.remove(
        'is-error',
        'is-success',
        'is-loading'
      );

      if (type) {
        status.classList.add(
          `is-${type}`
        );
      }
    }


    function setLoading(loading) {
      button.disabled =
        loading;

      if (loading) {
        button.dataset.originalText =
          button.textContent;

        button.textContent =
          'Calculando...';

      } else {
        button.textContent =
          button.dataset.originalText ||
          'Calcular';
      }
    }


    async function calculate() {
      results.innerHTML = '';

      setStatus('', null);

      const postalCode =
        onlyDigits(input.value);

      if (
        postalCode.length !== 8
      ) {
        setStatus(
          'Digite um CEP válido com 8 números.',
          'error'
        );

        input.focus();

        return;
      }

      const variantId =
        getCurrentVariantId(root);

      if (!variantId) {
        setStatus(
          'Não foi possível identificar a variante selecionada.',
          'error'
        );

        return;
      }

      const quantity =
        getQuantity(root);

      let temporaryLineKey = null;

      setLoading(true);

      setStatus(
        'Calculando as opções de entrega...',
        'loading'
      );


      try {
        const address =
          await resolvePostalCode(
            postalCode
          );

        await cleanupStaleSimulations();

        const temporaryItem =
          await addTemporaryProduct(
            variantId,
            quantity
          );

        temporaryLineKey =
          temporaryItem.key;

        await prepareShippingRates(
          address
        );

        const rates =
          await waitForShippingRates(
            address
          );

        await removeCartLineWithRetry(
          temporaryLineKey
        );

        temporaryLineKey = null;


        if (!rates.length) {
          setStatus(
            'Nenhuma opção de entrega foi encontrada para este CEP.',
            'error'
          );

          return;
        }


        const sortedRates =
          [...rates].sort(
            (a, b) =>
              Number(a.price || 0) -
              Number(b.price || 0)
          );


        results.innerHTML =
          sortedRates
            .map(renderRate)
            .join('');


        setStatus(
          `Opções de entrega para ${address.city} - ${address.provinceCode}.`,
          'success'
        );


      } catch (error) {
        console.error(
          'Erro na calculadora de frete:',
          error
        );

        setStatus(
          error.message ||
          'Não foi possível calcular o frete. Tente novamente.',
          'error'
        );

      } finally {
        if (temporaryLineKey) {
          try {
            await removeCartLineWithRetry(
              temporaryLineKey
            );

          } catch (cleanupError) {
            console.error(
              'Falha ao remover a linha temporária:',
              cleanupError
            );
          }
        }

        setLoading(false);
      }
    }


    form.addEventListener(
      'submit',
      function(event) {
        event.preventDefault();
        event.stopPropagation();

        calculate();
      }
    );


    input.addEventListener(
      'input',
      function() {
        input.value =
          formatPostalCode(
            input.value
          );
      }
    );


    const productScope =
      getProductScope(root);


    productScope.addEventListener(
      'change',
      function(event) {
        const target =
          event.target;

        if (!target) {
          return;
        }

        if (
          target.matches(
            '[name="id"], [name="quantity"], .quantity__input, select, input[type="radio"]'
          )
        ) {
          results.innerHTML = '';
          setStatus('', null);
        }
      }
    );
  }


  /* ==========================================================
     INIT
     ========================================================== */

  function init(scope) {
    const container =
      scope || document;

    const calculators =
      container.querySelectorAll(
        '[data-inspira-shipping]'
      );

    calculators.forEach(
      initCalculator
    );
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      function() {
        init(document);
      },
      {
        once: true
      }
    );

  } else {
    init(document);
  }


  document.addEventListener(
    'shopify:section:load',
    function(event) {
      init(event.target);
    }
  );

})();